import React, { useState, useMemo, useRef, useCallback } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ChatCore, ContactChatHeader } from '@/features/chat';
import { TaskFormModal } from '@/components/agenda/TaskFormModal';
import { TaskDetailSheet } from '@/components/tasks/TaskDetailSheet';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNotes } from '@/hooks/useNotes';
import { useCalls } from '@/hooks/useCalls';
import { useMeetings } from '@/hooks/useMeetings';
import { useAppUsers } from '@/hooks/useAppUsers';
import type { FilteredTask } from '@/hooks/useFilteredTasks';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  StickyNote, Phone, MessageCircle, Video, CheckCircle2, Circle,
  Plus, Filter, FileText, PhoneIncoming, PhoneOutgoing, Clock,
  ChevronDown, ChevronUp, Send, Loader2, Play, Pause, Sparkles,
  Calendar, Users, User, XCircle, AlertCircle
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getEffectiveCallDuration } from '@/lib/callDuration';

interface DealActivityTimelineProps {
  dealId: string;
  contactId?: string;
  contactPhone?: string;
  contactName?: string;
  dealName?: string;
  isEditable?: boolean;
}

type ActivityType = 'note' | 'call' | 'message' | 'task';
type FilterType = 'all' | ActivityType;

interface TimelineItem {
  id: string;
  type: ActivityType;
  title: string;
  description?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

const formatDuration = (seconds: number) => {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export function DealActivityTimeline({ dealId, contactId, contactPhone, contactName, dealName, isEditable = true }: DealActivityTimelineProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [selectedTask, setSelectedTask] = useState<FilteredTask | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);

  // Audio player state
  const [playingRecordingId, setPlayingRecordingId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Data hooks
  const { notes, loading: notesLoading, createNote } = useNotes({ dealId });
  const { calls, loading: callsLoading } = useCalls(contactId || null, null, dealId);
  const { meetings, loading: meetingsLoading } = useMeetings(dealId);
  const { users } = useAppUsers();
  const queryClient = useQueryClient();

  // Tasks
  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['deal-tasks-timeline', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('deal_id', dealId as any)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!dealId,
  });

  // WhatsApp messages (last 50)
  const { data: whatsappMessages = [], isLoading: messagesLoading } = useQuery({
    queryKey: ['deal-whatsapp-timeline', contactId],
    queryFn: async () => {
      if (!contactId) return [];
      const { data, error } = await (supabase as any)
        .from('messages')
        .select('*, sender:app_users!contact_messages_sent_by_app_user_id_fkey(display_name)')
        .eq('contact_id', contactId)
        .eq('channel', 'whatsapp')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    },
    enabled: !!contactId,
  });

  // Video meeting durations (source of truth for video calls).
  // The Daily.co webhook sometimes underestimates calls.duration_seconds
  // dramatically (e.g. 5s for a 19min call). The `meetings` table holds the
  // real duration_minutes — we index it by daily_recording_id and daily_room_url
  // so we can take MAX(call, recording, meeting) when displaying.
  const { data: videoMeetingDurations = new Map<string, number>() } = useQuery({
    queryKey: ['deal-video-meeting-durations', dealId, contactId],
    queryFn: async () => {
      const map = new Map<string, number>();
      const filters: string[] = [];
      if (dealId) filters.push(`deal_id.eq.${dealId}`);
      if (contactId) filters.push(`contact_id.eq.${contactId}`);
      if (filters.length === 0) return map;
      const { data, error } = await supabase
        .from('meetings')
        .select('daily_recording_id, daily_room_url, duration_minutes')
        .or(filters.join(','))
        .not('duration_minutes', 'is', null)
        .gt('duration_minutes', 0);
      if (error) {
        console.warn('Failed to fetch video meeting durations:', error);
        return map;
      }
      (data || []).forEach((m: any) => {
        const seconds = (m.duration_minutes || 0) * 60;
        if (m.daily_recording_id) map.set(`rec:${m.daily_recording_id}`, seconds);
        if (m.daily_room_url) map.set(`url:${m.daily_room_url}`, seconds);
      });
      return map;
    },
    enabled: !!(dealId || contactId),
  });

  const toggleExpanded = useCallback((id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Audio playback
  const handlePlayRecording = useCallback(async (recording: any) => {
    if (playingRecordingId === recording.id && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    if (playingRecordingId === recording.id && !isPlaying && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
      return;
    }

    // Stop current
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingRecordingId(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    try {
      const { data } = await supabase.storage.from('call-recordings').createSignedUrl(recording.storage_path, 3600);
      if (data?.signedUrl) {
        const audio = new Audio(data.signedUrl);
        audioRef.current = audio;
        audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
        audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
        audio.addEventListener('canplay', () => { setPlayingRecordingId(recording.id); setIsPlaying(true); });
        audio.addEventListener('ended', () => { setPlayingRecordingId(null); setIsPlaying(false); setCurrentTime(0); });
        audio.addEventListener('error', () => { toast.error('Erro ao reproduzir áudio'); setPlayingRecordingId(null); setIsPlaying(false); });
        audio.play().catch(() => { toast.error('Não foi possível reproduzir'); setPlayingRecordingId(null); setIsPlaying(false); });
      } else {
        toast.error('Não foi possível obter URL da gravação');
      }
    } catch {
      toast.error('Erro ao reproduzir gravação');
    }
  }, [playingRecordingId, isPlaying]);

  const handleSeek = useCallback((value: number[]) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  }, []);

  // Build unified timeline
  const timelineItems = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    (notes || []).forEach(note => {
      items.push({
        id: `note-${note.id}`,
        type: 'note',
        title: note.creator?.display_name ? `${note.creator.display_name} adicionou uma nota` : 'Nota adicionada',
        description: note.content,
        timestamp: note.created_at,
        metadata: note,
      });
    });

    (calls || []).forEach((call: any) => {
      const isInbound = call.direction === 'inbound';
      const isScheduled = call.status === 'scheduled' || call.status === 'in-progress';
      const isRescheduled = call.status === 'rescheduled';
      const statusLabel = call.status === 'completed' ? 'Realizada'
        : call.status === 'missed' ? 'Perdida'
        : call.status === 'scheduled' ? 'Agendada'
        : call.status === 'in-progress' ? 'Em andamento'
        : call.status === 'rescheduled' ? 'Remarcada'
        : call.status === 'no-show' || call.status === 'no_show' ? 'Não compareceu'
        : call.status === 'cancelled' || call.status === 'canceled' ? 'Cancelada'
        : call.status;
      // Para chamadas agendadas/futuras, ordenar pelo scheduled_for (não pelo created_at)
      // para que apareçam no dia correto da timeline.
      const timestamp = isScheduled && call.scheduled_for ? call.scheduled_for : call.created_at;
      const baseChannelLabel = call.channel === 'video' ? 'Videochamada' : 'Chamada';
      const realizadaSuffix = isInbound ? 'recebida' : 'realizada';
      const channelLabel = call.channel === 'video' ? 'Videochamada' : `Chamada ${realizadaSuffix}`;
      const title = isScheduled
        ? `${baseChannelLabel} agendada`
        : isRescheduled
          ? `${baseChannelLabel} remarcada`
          : channelLabel;
      const effectiveDuration = getEffectiveCallDuration(call, videoMeetingDurations);
      // Detectar agendamento "sem horário definido" (padrão T12:00:00Z)
      const scheduledDate = call.scheduled_for ? new Date(call.scheduled_for) : null;
      const isAllDayScheduled = !!scheduledDate && (
        (scheduledDate.getUTCHours() === 12 && scheduledDate.getUTCMinutes() === 0 && scheduledDate.getUTCSeconds() === 0) ||
        (scheduledDate.getHours() === 0 && scheduledDate.getMinutes() === 0)
      );
      const scheduledDescription = scheduledDate
        ? isAllDayScheduled
          ? `Agendada para ${safeFormat(scheduledDate, 'dd/MM/yyyy', { locale: ptBR })} (sem horário definido)`
          : `Agendada para ${safeFormat(scheduledDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`
        : null;
      items.push({
        id: `call-${call.id}`,
        type: 'call',
        title,
        description: effectiveDuration > 0
          ? `Duração: ${formatDuration(effectiveDuration)}`
          : isScheduled && scheduledDescription
            ? scheduledDescription
            : statusLabel,
        timestamp,
        metadata: { ...call, statusLabel, isScheduled, effectiveDuration },
      });
    });

    whatsappMessages.forEach((msg: any) => {
      const isOutbound = msg.direction === 'outbound';
      items.push({
        id: `msg-${msg.id}`,
        type: 'message',
        title: isOutbound ? 'Mensagem enviada' : 'Mensagem recebida',
        description: msg.body?.substring(0, 120) || msg.template_name || 'Mídia',
        timestamp: msg.created_at,
        metadata: msg,
      });
    });

    // Reuniões (calendar_events) NÃO são mais renderizadas como cards separados:
    // a videochamada já aparece via tabela `calls` (com resumo IA, gravação e duração).
    // Mantemos `useMeetings` apenas para alimentar `videoMeetingDurations`.


    tasks.forEach((task: any) => {
      items.push({
        id: `task-${task.id}`,
        type: 'task',
        title: task.title,
        description: task.description,
        timestamp: task.completed_at || task.created_at,
        metadata: task,
      });
    });

    items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    if (filter !== 'all') {
      return items.filter(item => item.type === filter);
    }
    return items;
  }, [notes, calls, whatsappMessages, meetings, tasks, filter, videoMeetingDurations]);

  // Group by day
  const groupedItems = useMemo(() => {
    const groups: { label: string; items: TimelineItem[] }[] = [];
    const groupMap = new Map<string, TimelineItem[]>();

    timelineItems.forEach(item => {
      const date = new Date(item.timestamp);
      let label: string;
      if (isToday(date)) label = 'Hoje';
      else if (isYesterday(date)) label = 'Ontem';
      else label = safeFormat(date, "dd 'de' MMMM", { locale: ptBR });

      if (!groupMap.has(label)) groupMap.set(label, []);
      groupMap.get(label)!.push(item);
    });

    groupMap.forEach((items, label) => groups.push({ label, items }));
    return groups;
  }, [timelineItems]);

  // Pending tasks
  const pendingTasks = useMemo(() => {
    return tasks.filter((t: any) => t.status !== 'completed');
  }, [tasks]);

  const isLoading = notesLoading || callsLoading || meetingsLoading || tasksLoading || messagesLoading;

  // Task detail handlers
  const handleOpenTaskDetail = useCallback((task: any) => {
    setSelectedTask(task as FilteredTask);
    setIsTaskDetailOpen(true);
  }, []);

  const handleUpdateTask = useCallback(async (id: string, data: Partial<FilteredTask>) => {
    const { error } = await supabase.from('tasks').update(data as any).eq('id', id);
    if (error) { toast.error('Erro ao atualizar tarefa'); return; }
    queryClient.invalidateQueries({ queryKey: ['deal-tasks-timeline'] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    toast.success('Tarefa atualizada!');
  }, [queryClient]);

  const handleDeleteTask = useCallback(async (id: string) => {
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (error) { toast.error('Erro ao excluir tarefa'); return; }
    queryClient.invalidateQueries({ queryKey: ['deal-tasks-timeline'] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    setIsTaskDetailOpen(false);
    toast.success('Tarefa excluída!');
  }, [queryClient]);

  const handleCompleteTask = useCallback((task: FilteredTask) => {
    handleUpdateTask(task.id, { status: 'completed', completed_at: new Date().toISOString() });
    setIsTaskDetailOpen(false);
  }, [handleUpdateTask]);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    setIsSubmitting(true);
    try {
      await createNote({ content: newNoteContent });
      setNewNoteContent('');
      setShowNoteForm(false);
      toast.success('Nota adicionada');
    } catch {
      toast.error('Erro ao adicionar nota');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filterOptions: { value: FilterType; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'Tudo', icon: <Filter className="w-3 h-3" /> },
    { value: 'task', label: 'Tarefas', icon: <CheckCircle2 className="w-3 h-3" /> },
    { value: 'note', label: 'Notas', icon: <StickyNote className="w-3 h-3" /> },
    { value: 'call', label: 'Chamadas', icon: <Phone className="w-3 h-3" /> },
    { value: 'message', label: 'Mensagens', icon: <MessageCircle className="w-3 h-3" /> },
  ];

  const typeConfig: Record<ActivityType, { icon: React.ReactNode; color: string }> = {
    note: { icon: <FileText className="w-4 h-4" />, color: 'text-amber-600 bg-amber-50 border-amber-200' },
    call: { icon: <Phone className="w-4 h-4" />, color: 'text-blue-600 bg-blue-50 border-blue-200' },
    message: { icon: <MessageCircle className="w-4 h-4" />, color: 'text-green-600 bg-green-50 border-green-200' },
    task: { icon: <CheckCircle2 className="w-4 h-4" />, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  };

  // ─── Render expanded content per type ───

  const renderNoteExpanded = (item: TimelineItem) => {
    const note = item.metadata;
    return (
      <div className="mt-3 pt-3 border-t border-border/50">
        <div className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">
          {note?.content || item.description}
        </div>
        {note?.creator?.display_name && (
          <p className="text-xs text-muted-foreground mt-2">
            Por: {note.creator.display_name}
            {note.created_at && ` • ${safeFormat(new Date(note.created_at), 'dd/MM/yyyy HH:mm')}`}
          </p>
        )}
      </div>
    );
  };

  const renderCallExpanded = (item: TimelineItem) => {
    const call = item.metadata;
    if (!call) return null;

    // Parse notes (same logic as CallsTab)
    let parsedNotes: any[] = [];
    if (call.notes) {
      try {
        parsedNotes = typeof call.notes === 'string' ? JSON.parse(call.notes) : call.notes;
        if (!Array.isArray(parsedNotes)) parsedNotes = [];
      } catch {
        parsedNotes = [];
      }
      if (parsedNotes.length === 0 && typeof call.notes === 'string' && call.notes.trim().length > 0) {
        parsedNotes = [{ text: call.notes, created_by: 'IA', created_date: null }];
      }
    }

    const effectiveDuration = call.effectiveDuration ?? getEffectiveCallDuration(call, videoMeetingDurations);

    return (
      <div className="mt-3 pt-3 border-t border-border/50 space-y-3">
        {/* Call metadata */}
        <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
          <Badge variant="outline" className="text-[10px]">
            {call.direction === 'inbound' ? 'Recebida' : 'Realizada'}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {call.statusLabel || call.status}
          </Badge>
          {call.channel === 'video' && (
            <Badge variant="outline" className="text-[10px]">
              <Video className="w-3 h-3 mr-1" />
              Vídeo
            </Badge>
          )}
          {effectiveDuration > 0 && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDuration(effectiveDuration)}
            </span>
          )}
          {call.from_e164 && <span>De: {call.from_e164}</span>}
          {call.to_e164 && <span>Para: {call.to_e164}</span>}
        </div>

        {/* Audio Recordings */}
        {call.recordings && call.recordings.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Gravações:</p>
            {call.recordings.map((recording: any) => {
              const isThisPlaying = playingRecordingId === recording.id;
              return (
                <div key={recording.id} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant={isThisPlaying && isPlaying ? "default" : "outline"}
                      onClick={(e) => { e.stopPropagation(); handlePlayRecording(recording); }}
                      className={cn("flex items-center gap-2", isThisPlaying && isPlaying && 'animate-pulse')}
                    >
                      {isThisPlaying && isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                      {recording.duration_seconds ? formatDuration(recording.duration_seconds) : 'Reproduzir'}
                    </Button>
                  </div>
                  {isThisPlaying && duration > 0 && (
                    <div className="flex items-center gap-3 px-2">
                      <span className="text-xs text-muted-foreground min-w-[40px]">{formatDuration(currentTime)}</span>
                      <Slider value={[currentTime]} max={duration} step={0.1} onValueChange={handleSeek} className="flex-1" />
                      <span className="text-xs text-muted-foreground min-w-[40px]">{formatDuration(duration)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Video Recording Link */}
        {call.channel === 'video' && call.metadata?.daily_recording_url && (
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={async (e) => {
                e.stopPropagation();
                const newTab = window.open('about:blank', '_blank');
                try {
                  let url = call.metadata.daily_recording_url;
                  try {
                    const head = await fetch(url, { method: 'HEAD' });
                    if (head.ok) { newTab!.location.href = url; return; }
                  } catch {}
                  const payload: any = {};
                  if (call.metadata.daily_recording_id) payload.recording_id = call.metadata.daily_recording_id;
                  else if (call.metadata.daily_room_name) payload.room_name = call.metadata.daily_room_name;
                  else { newTab?.close(); toast.error('Sem dados para renovar o link da gravação'); return; }
                  const { data, error } = await supabase.functions.invoke('get-daily-recording-access-link', { body: payload });
                  if (error || !data?.download_link) { newTab?.close(); toast.error('Não foi possível obter o link da gravação'); return; }
                  newTab!.location.href = data.download_link;
                } catch { newTab?.close(); toast.error('Erro ao abrir gravação'); }
              }}
              className="flex items-center gap-2"
            >
              <Play className="w-3 h-3" />
              Ver Gravação de Vídeo
            </Button>
          </div>
        )}

        {/* Scheduled date info (for upcoming/scheduled calls) */}
        {call.isScheduled && call.scheduled_for && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
            <Calendar className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <p className="font-medium text-amber-900 dark:text-amber-200">
                Agendada para {safeFormat(new Date(call.scheduled_for), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
              <p className="text-amber-700 dark:text-amber-400 mt-0.5">
                A chamada ainda não aconteceu. O resumo IA será gerado após a conclusão.
              </p>
            </div>
          </div>
        )}

        {/* AI Summary status fallback (for completed calls without summary) */}
        {!call.isScheduled && call.status === 'completed' && parsedNotes.length === 0 && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-muted/40 border border-border/50">
            <Sparkles className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="text-xs text-muted-foreground">
              {effectiveDuration > 0 && effectiveDuration < 60 ? (
                <>
                  <p className="font-medium text-foreground">Resumo IA não gerado</p>
                  <p className="mt-0.5">
                    Chamadas com menos de 60 segundos ({formatDuration(effectiveDuration)}) não geram resumo automático.
                  </p>
                </>
              ) : effectiveDuration >= 60 ? (
                <>
                  <p className="font-medium text-foreground">Resumo IA indisponível</p>
                  <p className="mt-0.5">
                    O resumo pode estar em processamento ou a transcrição falhou. Tente novamente mais tarde.
                  </p>
                </>
              ) : (
                <p>Sem gravação disponível para gerar resumo IA.</p>
              )}
            </div>
          </div>
        )}

        {/* Notes / AI Summary */}
        {parsedNotes.length > 0 && (
          <div className="space-y-2">
            {parsedNotes.map((note: any, index: number) => {
              const isAI = note.created_by === 'IA' || (note.text || '').startsWith('[Resumo IA]');
              return (
                <Card key={index} className={isAI
                  ? "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800"
                  : "bg-muted/30"
                }>
                  <CardContent className="p-3">
                    {isAI ? (
                      <div>
                        <Badge variant="secondary" className="bg-blue-600 text-white mb-2 text-xs">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Resumo IA
                        </Badge>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h2: ({ node, ...props }) => (
                              <h2 className="text-base font-bold text-primary mb-2 mt-4 first:mt-0 border-b border-blue-200 dark:border-blue-800 pb-1" {...props} />
                            ),
                            h3: ({ node, ...props }) => (
                              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 mt-3" {...props} />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul className="space-y-1 mb-3 ml-2" {...props} />
                            ),
                            li: ({ node, ...props }) => (
                              <li className="flex items-start gap-1.5 text-sm text-slate-600 dark:text-slate-400" {...props}>
                                <span className="text-blue-400 mt-1">•</span>
                                <span className="flex-1">{props.children}</span>
                              </li>
                            ),
                            strong: ({ node, ...props }) => (
                              <strong className="font-semibold text-slate-900 dark:text-slate-100" {...props} />
                            ),
                            p: ({ node, ...props }) => (
                              <p className="text-sm text-slate-700 dark:text-slate-300 mb-2 leading-relaxed" {...props} />
                            ),
                            hr: ({ node, ...props }) => (
                              <hr className="my-4 border-t border-blue-200 dark:border-blue-800" {...props} />
                            ),
                          }}
                        >
                          {(note.text || note.content || '').replace(/^\[Resumo IA\]\s*/, '')}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap text-foreground">
                        {note.text || note.content}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      {note.created_date && safeFormat(new Date(note.created_date), 'dd/MM/yyyy HH:mm')}
                      {note.created_by && ` • ${note.created_by}`}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMeetingExpanded = (item: TimelineItem) => {
    const meeting = item.metadata;
    if (!meeting) return null;

    const statusColors: Record<string, string> = {
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      no_show: 'bg-orange-100 text-orange-800 border-orange-200',
      rescheduled: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    };

    return (
      <div className="mt-3 pt-3 border-t border-border/50 space-y-3">
        {/* Status + Date */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Badge className={statusColors[meeting.status] || 'bg-muted text-muted-foreground'}>
            {meeting.status}
          </Badge>
          {meeting.scheduled_at && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="w-3 h-3" />
              {safeFormat(new Date(meeting.scheduled_at), 'dd/MM/yyyy HH:mm')}
            </span>
          )}
        </div>

        {/* Description */}
        {meeting.description && (
          <p className="text-sm text-foreground whitespace-pre-wrap">{meeting.description}</p>
        )}

        {/* Attendees */}
        {meeting.attendees && Array.isArray(meeting.attendees) && meeting.attendees.length > 0 && (
          <div className="flex items-start gap-2 text-xs">
            <Users className="w-3.5 h-3.5 mt-0.5 text-muted-foreground" />
            <div className="flex flex-wrap gap-1">
              {meeting.attendees.map((email: string, idx: number) => (
                <Badge key={idx} variant="secondary" className="text-[10px]">{email}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Meeting URL */}
        {meeting.meeting_url && (
          <div className="flex gap-2">
            {meeting.meeting_url.includes('daily.co') ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const win = window.open('', '_blank');
                    try {
                      let roomName = '';
                      try { roomName = new URL(meeting.meeting_url).pathname.replace(/^\//, ''); }
                      catch { roomName = meeting.meeting_url.split('/').pop() || ''; }
                      if (!roomName) { win?.close(); return; }
                      const { data } = await supabase.rpc('get_meeting_recording_url', { p_room_name: roomName });
                      const row = Array.isArray(data) ? data[0] : data;
                      let url = row?.recording_url;
                      if (!url && meeting.metadata?.daily_recording_url) url = meeting.metadata.daily_recording_url;
                      if (!url && row?.daily_recording_id) {
                        const { data: freshLink } = await supabase.functions.invoke('get-daily-recording-access-link', { body: { recording_id: row.daily_recording_id } });
                        url = freshLink?.download_link;
                      }
                      if (!url) {
                        const { data: freshLink } = await supabase.functions.invoke('get-daily-recording-access-link', { body: { room_name: roomName } });
                        url = freshLink?.download_link;
                      }
                      if (url && win) win.location.href = url;
                      else { win?.close(); toast.info('Gravação ainda não disponível'); }
                    } catch { win?.close(); toast.error('Erro ao buscar gravação'); }
                  }}
                >
                  <Video className="w-3 h-3 mr-1" />
                  Vídeo
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async (e) => {
                    e.stopPropagation();
                    let roomName = '';
                    try { roomName = new URL(meeting.meeting_url).pathname.replace(/^\//, ''); }
                    catch { roomName = meeting.meeting_url.split('/').pop() || ''; }
                    if (!roomName) return;
                    const { data } = await supabase.rpc('get_meeting_recording_url', { p_room_name: roomName });
                    const row = Array.isArray(data) ? data[0] : data;
                    if ((row as any)?.audio_url) window.open((row as any).audio_url, '_blank');
                    else toast.info('Áudio MP3 ainda não disponível');
                  }}
                >
                  <Play className="w-3 h-3 mr-1" />
                  Áudio (MP3)
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); window.open(meeting.meeting_url, '_blank'); }}>
                <Video className="w-3 h-3 mr-1" />
                Abrir Reunião
              </Button>
            )}
          </div>
        )}

        {/* Transcript */}
        {meeting.transcript && (
          <div className="p-3 rounded-lg bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-100 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 text-blue-700 border-blue-200 text-xs">
                <Sparkles className="w-3 h-3 mr-1" />
                Transcrição IA
              </Badge>
            </div>
            <pre className="text-xs text-foreground whitespace-pre-wrap font-sans leading-relaxed max-h-60 overflow-y-auto">
              {meeting.transcript}
            </pre>
          </div>
        )}
      </div>
    );
  };

  const renderMessageExpanded = (item: TimelineItem) => {
    const msg = item.metadata;
    if (!msg) return null;
    return (
      <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="text-[10px]">
            {msg.direction === 'outbound' ? 'Enviada' : 'Recebida'}
          </Badge>
          {msg.direction === 'outbound' && msg.sender?.display_name && (
            <span>Por: {msg.sender.display_name}</span>
          )}
          {msg.from_e164 && <span>De: {msg.from_e164}</span>}
          {msg.to_e164 && <span>Para: {msg.to_e164}</span>}
        </div>
        <div className="text-sm whitespace-pre-wrap text-foreground">
          {msg.body || msg.template_name || 'Sem conteúdo de texto'}
        </div>
        {(msg.media_storage_path || msg.media_url) && (
          <div className="mt-1">
            <Badge variant="outline" className="text-[10px]">📎 {msg.media_type || 'Mídia'} anexada</Badge>
          </div>
        )}
      </div>
    );
  };

  const renderTaskExpanded = (item: TimelineItem) => {
    const task = item.metadata;
    if (!task) return null;
    return (
      <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
        {task.description && (
          <p className="text-sm whitespace-pre-wrap text-foreground">{task.description}</p>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          {task.due_date && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Prazo: {safeFormat(new Date(task.due_date), 'dd/MM/yyyy')}
            </span>
          )}
          {task.priority && <Badge variant="outline" className="text-[10px]">{task.priority}</Badge>}
          {task.status && <Badge variant="outline" className="text-[10px] capitalize">{task.status}</Badge>}
        </div>
      </div>
    );
  };

  const renderExpandedContent = (item: TimelineItem) => {
    switch (item.type) {
      case 'note': return renderNoteExpanded(item);
      case 'call': return renderCallExpanded(item);
      case 'message': return renderMessageExpanded(item);
      case 'task': return renderTaskExpanded(item);
      default: return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters with inline action buttons */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {filterOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => {
              setFilter(opt.value);
              setShowNoteForm(opt.value === 'note' && isEditable);
            }}
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors rounded-full',
              filter === opt.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        ))}

        {isEditable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 transition-colors ml-auto"
                title="Ações rápidas"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => { setFilter('note'); setShowNoteForm(true); }}>
                <StickyNote className="w-4 h-4 mr-2" />
                Adicionar Nota
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTaskModalOpen(true)}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Criar Tarefa
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowWhatsApp(true)}>
                <MessageCircle className="w-4 h-4 mr-2" />
                Enviar Mensagem
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Contextual action button for tasks filter */}
      {filter === 'task' && isEditable && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setTaskModalOpen(true)}
          className="w-full border-dashed"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Adicionar Tarefa
        </Button>
      )}

      {/* Contextual action button for messages filter */}
      {filter === 'message' && isEditable && contactId && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowWhatsApp(true)}
          className="w-full border-dashed"
        >
          <MessageCircle className="w-3.5 h-3.5 mr-1" />
          Enviar Mensagem
        </Button>
      )}

      {/* Inline Note Form */}
      {showNoteForm && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="p-3 space-y-2">
            <Textarea
              value={newNoteContent}
              onChange={e => setNewNoteContent(e.target.value)}
              placeholder="Escreva uma nota..."
              rows={3}
              className="bg-white"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowNoteForm(false); setNewNoteContent(''); }}>
                Cancelar
              </Button>
              <Button size="sm" onClick={handleAddNote} disabled={isSubmitting || !newNoteContent.trim()}>
                {isSubmitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                Salvar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Tasks Section */}
      {(filter === 'all' || filter === 'task') && pendingTasks.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Circle className="w-3.5 h-3.5 text-amber-500" />
              Tarefas Pendentes ({pendingTasks.length})
            </h4>
            <div className="space-y-2">
              {pendingTasks.map((task: any) => (
                <div key={task.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer" onClick={() => handleOpenTaskDetail(task)}>
                  <Circle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{task.title}</p>
                    {task.due_date && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {safeFormat(new Date(task.due_date), 'dd/MM/yyyy')}
                      </p>
                    )}
                  </div>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0">
                    {task.priority || 'medium'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : groupedItems.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">Nenhuma atividade encontrada</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedItems.map(group => (
            <div key={group.label}>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-1">
                {group.label}
              </h4>
              <div className="space-y-2">
                {group.items.map(item => {
                  const config = typeConfig[item.type];
                  const isExpanded = expandedItems.has(item.id);
                  const isVideoCall = item.type === 'call' && item.metadata?.channel === 'video';
                  return (
                    <div
                      key={item.id}
                      className={cn(
                        'p-3 rounded-lg border transition-colors cursor-pointer',
                        item.type === 'message' && item.metadata?.direction === 'outbound'
                          ? 'text-green-700 bg-green-100 border-green-300'
                          : isVideoCall
                            ? 'text-purple-700 bg-purple-50 border-purple-200'
                            : config.color,
                        isExpanded && 'ring-1 ring-primary/20'
                      )}
                      onClick={() => toggleExpanded(item.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex-shrink-0">
                          {isVideoCall ? (
                            <Video className="w-4 h-4" />
                          ) : item.type === 'call' && item.metadata?.direction === 'inbound' ? (
                            <PhoneIncoming className="w-4 h-4" />
                          ) : item.type === 'call' && item.metadata?.direction === 'outbound' ? (
                            <PhoneOutgoing className="w-4 h-4" />
                          ) : (
                            config.icon
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{item.title}</p>
                            {item.type === 'call' && item.metadata?.statusLabel && (
                              <Badge variant="outline" className="text-[10px]">
                                {item.metadata.statusLabel}
                              </Badge>
                            )}
                            {item.type === 'call' && item.metadata?.recordings?.length > 0 && (
                              <Badge variant="outline" className="text-[10px]">
                                <Play className="w-2.5 h-2.5 mr-0.5" />
                                {item.metadata.recordings.length}
                              </Badge>
                            )}
                            {item.type === 'message' && (
                              <Badge variant="outline" className="text-[10px]">
                                {item.metadata?.direction === 'outbound' ? 'Enviada' : 'Recebida'}
                              </Badge>
                            )}
                            {item.type === 'task' && item.metadata?.status === 'completed' && (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                                Concluída
                              </Badge>
                            )}
                          </div>
                          {!isExpanded && item.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                            {safeFormat(new Date(item.timestamp), 'HH:mm')}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && renderExpandedContent(item)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Task Creation Modal */}
      <TaskFormModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        defaultContact={contactId && contactName ? { id: contactId, name: contactName } : null}
        defaultDeal={dealId && dealName ? { id: dealId, name: dealName } : { id: dealId, name: dealId }}
      />

      {/* WhatsApp Side Panel */}
      <Sheet open={showWhatsApp} onOpenChange={setShowWhatsApp}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col h-full">
          <SheetHeader className="p-4 border-b">
            <SheetTitle>WhatsApp — {contactName || 'Contato'}</SheetTitle>
            {contactPhone && <p className="text-sm text-muted-foreground">{contactPhone}</p>}
          </SheetHeader>
          <div className="flex-1 overflow-hidden">
            {showWhatsApp && contactId && (
              <ChatCore
                contactId={contactId}
                contactPhone={contactPhone}
                showConversationSelector
                header={
                  <ContactChatHeader
                    phoneNumber={contactPhone}
                    isConnected
                  />
                }
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Task Detail Sheet */}
      <TaskDetailSheet
        task={selectedTask}
        open={isTaskDetailOpen}
        onOpenChange={setIsTaskDetailOpen}
        users={users}
        onUpdate={handleUpdateTask}
        onDelete={handleDeleteTask}
        onComplete={handleCompleteTask}
      />
    </div>
  );
}
